import React, { useContext } from "react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const EmployeeStatsTable = ({
  data,
  pagination,
  setPagination,
  selectedRows,
  setSelectedRows,
  onSelectAll,
  withCheckbox = true,
}) => {
  // HOOKS
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();

  const standardHours = 160; // Standard working hours
  // Function to create chart data for working hours
  const getWorkingHoursChartData = (hours) => {
    const remainingHours = Math.max(0, standardHours - hours);

    // Determine color based on hours worked
    const getColor = () => {
      if (hours > 120) return ["#4CAF50", "#388E3C"]; // > 75% - Green
      if (hours > 80) return ["#FFC107", "#FFA000"]; // > 50% - Yellow
      return ["#F44336", "#D32F2F"]; // < 50% - Red
    };

    const [backgroundColor, borderColor] = getColor();

    return {
      labels: [t("workingHours"), t("reamainingHours")],
      datasets: [
        {
          data: [hours, remainingHours],
          backgroundColor: [backgroundColor, "#E0E0E0"],
          borderColor: [borderColor, "#BDBDBD"],
          borderWidth: 1,
        },
      ],
    };
  };

  // Table column titles
  const tableTitles = [
    { value: "employeeName", keepOnMobile: true },
    // { value: "month", keepOnMobile: true },
    { value: "status", keepOnMobile: true },
  ];

  // Function to prepare table rows from employee data
  const prepareTableRows = () => {
    return data.map((employee) => [
      {
        type: TABLE_CELL_TYPES.TEXT,
        value: employee.name,
        keepOnMobile: true,
      },

      {
        type: TABLE_CELL_TYPES.ELEMENT,
        value: (
          <>
            <div className="o-column o-column--vertical-align_center o-column--gap-size_400">
              <div className="o-row o-row--gap-size_200 o-row--vertical-align_center">
                <div style={{ width: "50px", height: "50px" }}>
                  <Pie
                    data={getWorkingHoursChartData(employee.workingHours)}
                    options={{
                      plugins: { legend: { display: false } },
                      maintainAspectRatio: false,
                    }}
                  />
                </div>

                <div className="u-margin--left_200 t-text-align_center">
                  <div className="t-bold_extra-bold">
                    {((employee.workingHours / standardHours) * 100).toFixed(2)}
                    %
                  </div>
                  <div
                    style={{ fontSize: "0.8em", color: "#666" }}
                    className=""
                  >
                    {employee?.workingHours?.toFixed(2)}/{standardHours} hours
                  </div>
                </div>
              </div>

              <div
                className="o-row o-row--gap-size_200 o-row--vertical-align_center"
                style={{ height: "50px" }}
              >
                <Bar
                  data={{
                    labels: ["Progress"],
                    datasets: [
                      {
                        label: "Completion %",
                        data: [Math.round((employee.workingHours / 160) * 100)],
                        backgroundColor: [
                          employee.workingHours > 120 // 75% of 160
                            ? "#4CAF50"
                            : employee.workingHours > 80 // 50% of 160
                            ? "#FFC107"
                            : "#F44336",
                        ],
                        borderColor: [
                          employee.workingHours > 120
                            ? "#388E3C"
                            : employee.workingHours > 80
                            ? "#FFA000"
                            : "#D32F2F",
                        ],
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y",
                    scales: {
                      x: {
                        max: 160,
                        min: 0,
                        ticks: {
                          stepSize: 8,
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    maintainAspectRatio: false,
                  }}
                />
              </div>
            </div>
          </>
        ),
        keepOnMobile: true,
      },
    ]);
  };

  const tableRows = prepareTableRows();

  return (
    <RoundedTable
      titles={tableTitles}
      rows={tableRows}
      fullData={data}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      pagination={pagination}
      setPagination={setPagination}
      onSelectAll={onSelectAll}
    />
  );
};

export default EmployeeStatsTable;
